import type { FC } from 'react';
import React, { useContext } from 'react';

import { IMAGE_TYPE } from './../../ricos-content';
import { RicosContext, RicosViewerContext } from './../../ricos-context';
import type { Node } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { Helpers, NodeViewRenderer, NodeViewRendererProps } from './../../ricos-types';

import { ViewerFigure } from '../../_shared/figure/viewer-figure';
import type { ImagePluginViewerConfig } from '../types';
import type { ImageClickActionConfig, ImageClickActionHandle } from './common-types';
import { Image } from './image';

export const ViewerImage: FC<NodeViewRendererProps> = ({
  node,
  nodeIndex,
  NodeViewContainer,
  RicosNodesRenderer,
}) => {
  const { config: viewerConfig, seoMode, helpers } = useContext(RicosViewerContext);
  const { isMobile } = useContext(RicosContext);
  const config = (viewerConfig[IMAGE_TYPE] ?? {}) as ImagePluginViewerConfig;
  const expand = getExpandFunction({
    helpers,
    config,
    nodeId: node.id,
    imageData: node.imageData,
  });

  const imageClickActionConfig = getImageClickActionConfig({
    imageData: node.imageData,
    config,
    expand,
  });

  return (
    <ViewerFigure
      node={node}
      nodeIndex={nodeIndex}
      NodeViewContainer={NodeViewContainer}
      RicosNodesRenderer={RicosNodesRenderer}
    >
      <Image
        node={node}
        imageClickActionConfig={imageClickActionConfig}
        expand={expand}
        config={config}
        seoMode={seoMode}
        helpers={helpers}
        isMobile={isMobile}
        shouldLazyLoadImages
      />
    </ViewerFigure>
  );
};

const ImageRenderer: NodeViewRenderer = ({ node, ...props }) => {
  return <ViewerImage key={node.id} node={node} {...props} />;
};

export default ImageRenderer;

const getImageClickActionConfig = ({
  imageData,
  config,
  expand,
}: {
  imageData: Node['imageData'];
  config?: ImagePluginViewerConfig;
  expand?: ImageClickActionHandle;
}): ImageClickActionConfig => {
  if (imageData?.link?.url && config) {
    return { type: 'link' };
  }

  if (imageData?.link?.anchor) {
    return { type: 'anchor' };
  }

  if (expand) {
    return { type: 'expand', onClick: expand };
  }

  return { type: 'regular' };
};

const getExpandFunction = ({
  helpers,
  config,
  nodeId,
  imageData,
}: {
  imageData: Node['imageData'];
  helpers: Helpers;
  config: ImagePluginViewerConfig;
  nodeId: string;
}): ImageClickActionHandle | undefined => {
  const { onExpand, disableExpand } = config;
  if (imageData?.disableExpand === true || disableExpand === true || !onExpand) {
    return;
  }

  return e => {
    e.preventDefault();
    e.stopPropagation();
    helpers.onViewerAction?.(IMAGE_TYPE, 'Click', 'expand_image');
    onExpand(nodeId);
  };
};
